import {
  FirestoreDataConverter,
  SnapshotOptions,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { Content, Template, RobotControlButton } from "@/types/Message";
import { UserRecord } from "@/types/User";

export const contentConverter: FirestoreDataConverter<Content> = {
  toFirestore: (content: Content) => {
    return {
      name: content.data.name,
      description: content.data.description,
      owner: content.data.owner,
      price: content.data.price,
      public: content.data.public,
      tags: content.data.tags,
      trusted: content.data.trusted,
      copyOf: content.data.copyOf,
      copyDate: content.data.copyDate,
      templatesInfo: content.data.templatesInfo,
    };
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): Content => {
    const vals = snapshot.data(options);
    return {
      id: snapshot.id,
      data: {
        name: vals?.name || "",
        description: vals?.description,
        owner: vals?.owner,
        price: vals?.price,
        public: vals?.public || false,
        tags: vals?.tags || [],
        trusted: vals?.trusted || false,
        copyOf: vals?.copyOf,
        copyDate: vals?.copyDate,
        templatesInfo: vals?.templatesInfo || [],
      },
    } as Content;
  },
};

export const buttonConverter: FirestoreDataConverter<RobotControlButton> = {
  toFirestore: (button: RobotControlButton) => {
    return button;
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): RobotControlButton => {
    const vals = snapshot.data(options);
    if (!vals) throw new Error("Document data is undefined");

    // If before splitting buttons by type
    if (!("type" in vals)) {
      return {
        type: "Message",
        labelColor: vals.color,
        details: {
          title: vals.title,
          speech: vals.speech,
          color: vals.color,
          emotion: vals.emotion,
        },
      };
    }
    return vals as RobotControlButton;
  },
};

export const templateConverter: FirestoreDataConverter<Template> = {
  toFirestore: (template: Template) => {
    // We don't want to save the ID in the document body if it's already the doc ID
    const data = { ...template };
    delete data.id;
    return data;
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): Template => {
    const vals = snapshot.data(options);
    if (!vals) throw new Error("Document data is undefined");

    const templateData = { ...vals, id: snapshot.id } as Template;

    if (templateData.buttons) {
      // Handle legacy migration directly in the converter
      templateData.buttons = Object.fromEntries(
        Object.entries(templateData.buttons).map(([buttonID, rawButton]) => {
          const button = rawButton as Record<string, unknown>;
          if (button.type) return [buttonID, button as RobotControlButton];

          return [
            buttonID,
            {
              type: "Message",
              labelColor: button.color,
              details: {
                title: button.title,
                speech: button.speech,
                color: button.color,
                emotion: button.emotion,
              },
            } as RobotControlButton,
          ];
        }),
      );
    }

    return templateData;
  },
};

export const userConverter: FirestoreDataConverter<UserRecord> = {
  toFirestore: (user: UserRecord) => {
    return user.data;
  },
  fromFirestore: (
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions,
  ): UserRecord => {
    const vals = snapshot.data(options);
    return {
      id: snapshot.id,
      data: {
        photoUrl: vals?.photoUrl,
        name: vals?.name,
        email: vals?.email,
        username: vals?.username,
        featureFlags: vals?.featureFlags,
      },
    };
  },
};
